/**
 * Product Reviews — Custom Element
 * Loads reviews from a static JSON file, shuffles, and renders
 * with fade-in/out card rotation.
 */
if (!customElements.get('product-reviews')) {
  customElements.define(
    'product-reviews',
    class ProductReviews extends HTMLElement {
      constructor() {
        super();
        this.reviews = [];
        this.shuffled = [];
        this.chunks = [];
        this.currentChunk = 0;
        this.timer = null;
        this.isPaused = false;
        this.isTransitioning = false;
        this.newestDate = null;
      }

      connectedCallback() {
        this.reviewsUrl = this.dataset.reviewsUrl;
        this.configPerPage = parseInt(this.dataset.perPage) || 3;
        this.rotationSpeed = (parseInt(this.dataset.rotationSpeed) || 6) * 1000;
        this.autoRotate = this.dataset.autoRotate !== 'false';
        this.showVerified = this.dataset.showVerified !== 'false';
        this.showHelpful = this.dataset.showHelpful !== 'false';
        this.showDistribution = this.dataset.showDistribution !== 'false';

        this.summaryEl = this.querySelector('#pr-summary');
        this.cardsEl = this.querySelector('#pr-cards');
        this.navEl = this.querySelector('#pr-nav');

        if (!this.reviewsUrl || !this.cardsEl) return;

        this.loadReviews();
        this.setupResize();
      }

      disconnectedCallback() {
        this.stopRotation();
        if (this._resizeHandler) {
          window.removeEventListener('resize', this._resizeHandler);
        }
      }

      /* ============================
         DATA LOADING
         ============================ */

      async loadReviews() {
        try {
          const resp = await fetch(this.reviewsUrl);
          if (!resp.ok) throw new Error('Failed to fetch reviews');
          this.reviews = await resp.json();
          if (!Array.isArray(this.reviews) || this.reviews.length === 0) {
            this.handleEmpty();
            return;
          }
          // Find the newest review date for relative date computation
          this.newestDate = this.reviews.reduce((max, r) => {
            const d = new Date(r.date);
            return d > max ? d : max;
          }, new Date(0));
          this.init();
        } catch (e) {
          // console.error('ProductReviews: Could not load reviews', e);
          this.handleEmpty();
        }
      }

      /* ============================
         INITIALIZATION
         ============================ */

      init() {
        this.renderSummary();
        this.perPage = this.getPerPage();
        this.shuffleAndChunk();
        this.renderCards(this.chunks[0]);
        this.renderNav();
        this.setupEvents();

        // Animate summary bars after a brief delay
        requestAnimationFrame(() => {
          setTimeout(() => this.animateBars(), 200);
        });

        // Show first cards
        requestAnimationFrame(() => {
          const cards = this.cardsEl.querySelectorAll('.pr-card');
          cards.forEach((card) => card.classList.add('pr-card--visible'));
        });

        if (this.autoRotate) {
          // Start rotation after the first full rotation period
          this.startRotation();
        }
      }

      /* ============================
         RESPONSIVE PER-PAGE
         ============================ */

      getPerPage() {
        const w = window.innerWidth;
        if (w < 600) return 1;
        if (w < 990) return Math.min(this.configPerPage, 2);
        return this.configPerPage;
      }

      setupResize() {
        let debounceTimer;
        this._resizeHandler = () => {
          clearTimeout(debounceTimer);
          debounceTimer = setTimeout(() => {
            const newPerPage = this.getPerPage();
            if (newPerPage !== this.perPage) {
              this.perPage = newPerPage;
              this.stopRotation();
              this.shuffleAndChunk();
              this.currentChunk = 0;
              this.renderCards(this.chunks[0]);
              this.renderNav();
              requestAnimationFrame(() => {
                this.cardsEl.querySelectorAll('.pr-card').forEach((c) => c.classList.add('pr-card--visible'));
              });
              if (this.autoRotate) this.startRotation();
            }
          }, 300);
        };
        window.addEventListener('resize', this._resizeHandler);
      }

      /* ============================
         SHUFFLE & CHUNK
         ============================ */

      shuffleAndChunk() {
        // Fisher-Yates shuffle
        this.shuffled = [...this.reviews];
        for (let i = this.shuffled.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [this.shuffled[i], this.shuffled[j]] = [this.shuffled[j], this.shuffled[i]];
        }
        // Chunk into pages
        this.chunks = [];
        for (let i = 0; i < this.shuffled.length; i += this.perPage) {
          this.chunks.push(this.shuffled.slice(i, i + this.perPage));
        }
      }

      /* ============================
         SUMMARY
         ============================ */

      computeStats() {
        const total = this.reviews.length;
        let sum = 0;
        const dist = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
        this.reviews.forEach((r) => {
          sum += r.rating;
          dist[r.rating] = (dist[r.rating] || 0) + 1;
        });
        const avg = (sum / total).toFixed(1);
        const pcts = {};
        for (let s = 5; s >= 1; s--) {
          pcts[s] = Math.round((dist[s] / total) * 100);
        }
        return { total, avg, avgRound: Math.round(sum / total), dist, pcts };
      }

      renderSummary() {
        if (!this.summaryEl) return;
        const stats = this.computeStats();

        let html = `
          <div class="pr-summary__score">
            <div class="pr-summary__avg">${stats.avg}</div>
            <div class="pr-summary__stars">${this.renderStars(stats.avgRound, 18)}</div>
            <div class="pr-summary__count">Based on ${stats.total} reviews</div>
          </div>`;

        if (this.showDistribution) {
          html += '<div class="pr-summary__distribution">';
          for (let s = 5; s >= 1; s--) {
            html += `
              <div class="pr-dist-row">
                <span class="pr-dist-row__label">${s}
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor" class="pr-star--filled">
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                  </svg>
                </span>
                <div class="pr-dist-row__bar">
                  <div class="pr-dist-row__fill" data-width="${stats.pcts[s]}"></div>
                </div>
                <span class="pr-dist-row__pct">${stats.pcts[s]}%</span>
              </div>`;
          }
          html += '</div>';
        }

        this.summaryEl.innerHTML = html;
      }

      animateBars() {
        this.querySelectorAll('.pr-dist-row__fill').forEach((bar) => {
          const w = bar.dataset.width;
          bar.style.width = w + '%';
        });
      }

      /* ============================
         CARDS
         ============================ */

      renderCards(chunk) {
        if (!chunk || !this.cardsEl) return;
        this.cardsEl.innerHTML = chunk.map((review, i) => this.buildCard(review, i)).join('');
      }

      buildCard(review, globalIdx) {
        const initials = this.getInitials(review.name);
        const gradient = this.getAvatarGradient(review.name);
        const stars = this.renderStars(review.rating, 15);
        const relDate = this.getRelativeDate(review.date);
        const helpful = this.getHelpfulCount(review.name);

        let verifiedHTML = '';
        if (this.showVerified) {
          verifiedHTML = `
            <span class="pr-card__verified">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm-1.25 17.292l-4.5-4.364 1.857-1.858 2.643 2.506 5.643-5.784 1.857 1.858-7.5 7.642z"/>
              </svg>
              Verified Purchase
            </span>`;
        }

        let helpfulHTML = '';
        if (this.showHelpful) {
          helpfulHTML = `
            <span class="pr-card__helpful">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3H14zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3"/>
              </svg>
              ${helpful} found helpful
            </span>`;
        }

        // Escape review text for safety
        const escapedReview = review.review
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;');

        return `
          <div class="pr-card">
            <div class="pr-card__header">
              <div class="pr-card__avatar" style="background: ${gradient}">${initials}</div>
              <div class="pr-card__author">
                <span class="pr-card__name">${review.name}</span>
                ${verifiedHTML}
              </div>
            </div>
            <div class="pr-card__stars">${stars}</div>
            <p class="pr-card__text">"${escapedReview}"</p>
            <div class="pr-card__footer">
              <span class="pr-card__date">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <circle cx="12" cy="12" r="10"/>
                  <polyline points="12 6 12 12 16 14"/>
                </svg>
                ${relDate}
              </span>
              ${helpfulHTML}
            </div>
          </div>`;
      }

      /* ============================
         TRANSITIONS
         ============================ */

      async transitionTo(index) {
        if (this.isTransitioning || index === this.currentChunk) return;
        this.isTransitioning = true;

        const oldCards = this.cardsEl.querySelectorAll('.pr-card');

        // Fade out current cards
        oldCards.forEach((card) => card.classList.remove('pr-card--visible'));

        // Wait for fade-out
        await this.wait(500);

        // Update chunk
        this.currentChunk = index;
        this.renderCards(this.chunks[index]);

        // Force reflow
        void this.cardsEl.offsetHeight;

        // Fade in new cards
        requestAnimationFrame(() => {
          this.cardsEl.querySelectorAll('.pr-card').forEach((card) => {
            card.classList.add('pr-card--visible');
          });
        });

        this.updateDots();
        this.isTransitioning = false;
      }

      next() {
        const nextIdx = (this.currentChunk + 1) % this.chunks.length;
        // Reshuffle when looping back to start
        if (nextIdx === 0) {
          this.shuffleAndChunk();
        }
        this.transitionTo(nextIdx);
      }

      prev() {
        const prevIdx = (this.currentChunk - 1 + this.chunks.length) % this.chunks.length;
        this.transitionTo(prevIdx);
      }

      /* ============================
         NAVIGATION
         ============================ */

      renderNav() {
        if (!this.navEl) return;
        const totalDots = Math.min(this.chunks.length, 10); // Cap dots at 10

        let dotsHTML = '';
        for (let i = 0; i < totalDots; i++) {
          dotsHTML += `<button class="pr-dot${i === 0 ? ' active' : ''}" data-index="${i}" aria-label="Go to reviews page ${i + 1}"></button>`;
        }

        this.navEl.innerHTML = `
          <button class="pr-nav-btn" data-dir="prev" aria-label="Previous reviews">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="15 18 9 12 15 6"/>
            </svg>
          </button>
          <div class="pr-nav-dots">${dotsHTML}</div>
          <button class="pr-nav-btn" data-dir="next" aria-label="Next reviews">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="9 18 15 12 9 6"/>
            </svg>
          </button>`;
      }

      updateDots() {
        if (!this.navEl) return;
        const dots = this.navEl.querySelectorAll('.pr-dot');
        const cappedIdx = this.currentChunk % dots.length;
        dots.forEach((dot, i) => {
          dot.classList.toggle('active', i === cappedIdx);
        });
      }

      /* ============================
         EVENTS
         ============================ */

      setupEvents() {
        // Nav buttons
        if (this.navEl) {
          this.navEl.addEventListener('click', (e) => {
            const btn = e.target.closest('[data-dir]');
            const dot = e.target.closest('.pr-dot');
            if (btn) {
              this.resetRotation();
              btn.dataset.dir === 'next' ? this.next() : this.prev();
            } else if (dot) {
              this.resetRotation();
              this.transitionTo(parseInt(dot.dataset.index));
            }
          });
        }

        // Hover pause
        const carousel = this.querySelector('.product-reviews__carousel');
        if (carousel) {
          carousel.addEventListener('mouseenter', () => {
            this.isPaused = true;
            this.stopRotation();
          });
          carousel.addEventListener('mouseleave', () => {
            this.isPaused = false;
            if (this.autoRotate) this.startRotation();
          });
        }

        // Touch swipe
        let touchStartX = 0;
        let touchStartY = 0;
        if (this.cardsEl) {
          this.cardsEl.addEventListener('touchstart', (e) => {
            touchStartX = e.touches[0].clientX;
            touchStartY = e.touches[0].clientY;
          }, { passive: true });

          this.cardsEl.addEventListener('touchend', (e) => {
            const dx = e.changedTouches[0].clientX - touchStartX;
            const dy = e.changedTouches[0].clientY - touchStartY;
            // Only trigger if horizontal swipe is dominant
            if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy)) {
              this.resetRotation();
              dx < 0 ? this.next() : this.prev();
            }
          }, { passive: true });
        }
      }

      /* ============================
         ROTATION TIMER
         ============================ */

      startRotation() {
        this.stopRotation();
        this.timer = setInterval(() => {
          if (!this.isPaused && !this.isTransitioning) {
            this.next();
          }
        }, this.rotationSpeed);
      }

      stopRotation() {
        if (this.timer) {
          clearInterval(this.timer);
          this.timer = null;
        }
      }

      resetRotation() {
        this.stopRotation();
        if (this.autoRotate && !this.isPaused) {
          this.startRotation();
        }
      }

      /* ============================
         UTILITIES
         ============================ */

      renderStars(rating, size) {
        let html = '';
        for (let i = 1; i <= 5; i++) {
          if (i <= rating) {
            html += `<svg class="pr-star--filled" width="${size}" height="${size}" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>`;
          } else {
            html += `<svg class="pr-star--empty" width="${size}" height="${size}" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>`;
          }
        }
        return html;
      }

      getRelativeDate(dateStr) {
        const reviewDate = new Date(dateStr);
        const today = new Date();
        // Shift all dates so the newest review = today
        const offset = today.getTime() - this.newestDate.getTime();
        const adjusted = new Date(reviewDate.getTime() + offset);
        const diff = today.getTime() - adjusted.getTime();

        const days = Math.floor(diff / (1000 * 60 * 60 * 24));
        if (days < 1) return 'Today';
        if (days === 1) return 'Yesterday';
        if (days < 7) return days + ' days ago';
        const weeks = Math.floor(days / 7);
        if (weeks === 1) return '1 week ago';
        if (weeks < 5) return weeks + ' weeks ago';
        const months = Math.floor(days / 30);
        if (months === 1) return '1 month ago';
        if (months < 12) return months + ' months ago';
        return '1 year ago';
      }

      getInitials(name) {
        return name
          .split(' ')
          .map((n) => n.charAt(0))
          .join('')
          .substring(0, 2)
          .toUpperCase();
      }

      getAvatarGradient(name) {
        const gradients = [
          'linear-gradient(135deg, #387373, #5CB7A9)',
          'linear-gradient(135deg, #254d4e, #4a9999)',
          'linear-gradient(135deg, #2D6A6B, #6bc5b8)',
          'linear-gradient(135deg, #1a3a3b, #3d8888)',
          'linear-gradient(135deg, #3d8888, #7ccfbf)',
          'linear-gradient(135deg, #2a5c5c, #5CB7A9)',
          'linear-gradient(135deg, #1e4d4e, #54a89a)',
        ];
        let hash = 0;
        for (let i = 0; i < name.length; i++) {
          hash = name.charCodeAt(i) + ((hash << 5) - hash);
        }
        return gradients[Math.abs(hash) % gradients.length];
      }

      getHelpfulCount(name) {
        // Deterministic "random" per name so it's consistent across page loads
        let hash = 0;
        for (let i = 0; i < name.length; i++) {
          hash = name.charCodeAt(i) + ((hash << 7) - hash);
        }
        return 2 + (Math.abs(hash) % 46); // Range: 2-47
      }

      wait(ms) {
        return new Promise((resolve) => setTimeout(resolve, ms));
      }

      handleEmpty() {
        if (this.summaryEl) this.summaryEl.innerHTML = '';
        if (this.cardsEl) {
          this.cardsEl.innerHTML = '<p style="text-align:center;color:rgba(255,255,255,0.4);font-family:Orbitron,sans-serif;padding:2rem;">Reviews coming soon</p>';
        }
        if (this.navEl) this.navEl.innerHTML = '';
      }
    }
  );
}
